import { HttpInterceptorFn } from '@angular/common/http';

import { environment } from '../../../environments/environment';
import { isUnderBase, trimTrailingSlash } from './base-url';
import { activeModuleApiBase, isModuleApiUrl } from './module-api';

const ABSOLUTE_URL = /^[a-z][a-z\d+\-.]*:\/\//i;

const UMC_BASE = trimTrailingSlash(environment.umc.baseUrl);

/**
 * Rewrites relative request URLs against the backend of the module the user is
 * inside (`environment.api.modules`), so a module can call
 * `http.get('/employees')` and stay environment-agnostic — and stay ignorant of
 * where this installation runs its service. The shell has no backend of its
 * own, so outside a module — or inside one that has no backend — a relative URL
 * is left alone.
 *
 * Left untouched:
 *
 * - absolute URLs — this is also what keeps Saphir Identity out of the prefix:
 *   the auth library always addresses it by its absolute `idpBaseUrl`;
 * - URLs already under a module backend, so prefixing stays idempotent;
 * - URLs under the UMC base — it is a different service with its own address,
 *   and prefixing would turn `/umc/me/context` into `/api/umc/me/context`,
 *   which is a 404 nobody can attribute (caught live exactly that way).
 *
 * Two kinds of request never reach this interceptor at all: the translation
 * dictionaries and `modules.config.json`, which load over `HttpBackend` and
 * `fetch` respectively. Both are static assets, and prefixing them onto a
 * backend would ask it for files that live next to `index.html`.
 */
export const apiUrlInterceptor: HttpInterceptorFn = (request, next) => {
  if (
    ABSOLUTE_URL.test(request.url) ||
    isModuleApiUrl(request.url) ||
    isUnderBase(request.url, UMC_BASE)
  ) {
    return next(request);
  }

  const base = activeModuleApiBase();

  if (base === undefined) {
    return next(request);
  }

  const path = request.url.startsWith('/') ? request.url : `/${request.url}`;

  return next(request.clone({ url: `${base}${path}` }));
};
